import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Layout from './layouts/Layout';
import { useAuth } from './auth/AuthProvider';

/**
 * Lazy-loaded page components
 *
 * Using React.lazy for code splitting to improve initial bundle size.
 * Each page is loaded on-demand when the route is accessed.
 *
 * @see https://react.dev/reference/react/lazy
 */
const HomePage = lazy(() => import('./pages/Home'));
const LoginPage = lazy(() => import('./pages/Login'));
const CoreReleasePage = lazy(() => import('./pages/CoreReleasePage'));
const HealthCheckPage = lazy(() => import('./pages/HealthCheckPage'));
const PlatformPage = lazy(() => import('./pages/PlatformPage'));
const PlatformDetailPage = lazy(() => import('./pages/PlatformDetailPage'));
const PlatformConfigPage = lazy(() => import('./pages/PlatformConfigPage'));
const PlatformEntityPage = lazy(() => import('./pages/PlatformEntityPage'));
const PlatformServicePage = lazy(() => import('./pages/PlatformServicePage'));
const PlatformVersionPage = lazy(() => import('./pages/PlatformVersionPage'));
const SdkPackagePage = lazy(() => import('./pages/SdkPackagePage'));
const SdkVersionPage = lazy(() => import('./pages/SdkVersionPage'));
const EntityEditorPage = lazy(() => import('./pages/EntityEditorPage'));
const NotFoundPage = lazy(() => import('./pages/NotFound'));

/**
 * Loading Fallback Component
 *
 * Displays a centered loading spinner while lazy-loaded
 * page components are being fetched.
 */
const PageLoadingFallback: React.FC = () => (
  <Box
sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
    }}  >
    <CircularProgress />
  </Box>
);

/**
 * Protected Route Wrapper
 *
 * Redirects unauthenticated users to the login page.
 * Used to protect routes that require authentication.
 *
 * @param children - Child components to render if authenticated
 * @param redirectTo - Path to redirect to if not authenticated
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/login',
}) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

/**
 * Application Routes Component
 *
 * Defines all routes for the application using React Router.
 * Uses Routes/Route components for declarative routing.
 *
 * Route Structure:
 * - / (Home page)
 * - /core-releases (CoreRelease list page)
 * - /health-checks (HealthCheck list page)
 * - /platforms (Platform list page)
 * - /platform-configs (PlatformConfig list page)
 * - /platform-entities (PlatformEntity list page)
 * - /platform-services (PlatformService list page)
 * - /platform-versions (PlatformVersion list page)
 * - /sdk-packages (SdkPackage list page)
 * - /sdk-versions (SdkVersion list page)
 * - /* (404 Not Found)
 *
 * @see https://reactrouter.com/en/main/components/routes
 */
export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Suspense fallback={<PageLoadingFallback />}>
            <LoginPage />
          </Suspense>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <HomePage />
            </Suspense>
          }
        />
        <Route
          path="core-releases"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <CoreReleasePage />
            </Suspense>
          }
        />
        <Route
          path="health-checks"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <HealthCheckPage />
            </Suspense>
          }
        />
        <Route
          path="platforms"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <PlatformPage />
            </Suspense>
          }
        />
        <Route
          path="platforms/:id"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <PlatformDetailPage />
            </Suspense>
          }
        />
        <Route
          path="platform-configs"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <PlatformConfigPage />
            </Suspense>
          }
        />
        <Route
          path="platform-entities"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <PlatformEntityPage />
            </Suspense>
          }
        />
        <Route
          path="platform-services"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <PlatformServicePage />
            </Suspense>
          }
        />
        <Route
          path="platform-versions"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <PlatformVersionPage />
            </Suspense>
          }
        />
        <Route
          path="entity-editor"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <EntityEditorPage />
            </Suspense>
          }
        />
        <Route
          path="sdk-packages"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <SdkPackagePage />
            </Suspense>
          }
        />
        <Route
          path="sdk-versions"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <SdkVersionPage />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
